import { createStore, applyMiddleware, compose, Store, Action } from "redux";
import createSagaMiddleware from "redux-saga";
import { persistStore, persistReducer, Persistor } from "redux-persist";
import storage from "redux-persist/lib/storage";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { createLogger } from "redux-logger";
import { PersistPartial } from "redux-persist/es/persistReducer";

import rootReducer from "./reducer";
import rootSaga from "./sagas";
import SetTransform from "./transforms";

const sagaMiddleware = createSagaMiddleware();

const persistConfig = {
  key: "root",
  storage,
  transforms: [SetTransform],
  stateReconciler: autoMergeLevel2,
};

const logger = createLogger({
  collapsed: true,
  predicate: () => process.env.NODE_ENV !== "production",
});

declare global {
  interface Window {
    // eslint-disable-next-line no-undef
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

const isDebugMode = process.env.NODE_ENV === "development";

export default function configureStore(): {
  store: Store<PersistPartial, Action<string>>;
  persistor: Persistor;
} {
  let middlewares = [];
  if (isDebugMode) {
    middlewares = [sagaMiddleware, logger];
  } else {
    middlewares = [sagaMiddleware];
  }

  const enhancers = [applyMiddleware(...middlewares)];

  const composeEnhancers =
    process.env.NODE_ENV !== "production" &&
    typeof window === "object" &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const persistedReducer = persistReducer(persistConfig, rootReducer);
  const store = createStore(
    persistedReducer,
    <{ _persist: { version: number; rehydrated: boolean } } | undefined>{},
    composeEnhancers(...enhancers)
  );
  const persistor = persistStore(store);

  sagaMiddleware.run(rootSaga);

  return { store, persistor };
}
